import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Overlay, Popover } from 'react-bootstrap';
import { useTranslate } from 'react-polyglot';

import styles from './PedigreeCard.module.scss';
import { general } from '../../../../../general';

// The stamtavle. On the breeding pages the dam (T) with her ancestors to the
// left and the sire (H) with his to the right; on the dog page (dog) the dog
// in the middle with its dam's line to the left and its sire's to the right.
// Each tree comes from
// GET /api/statistics/dog/:id/familytree, which adds HD, the dog's own
// diagnoses (health) and its siblings' (relatives) to every dog in the tree.
//
// Orange: the dog itself has a diagnosis; the lines from it down to T/H are
// orange too. Strong orange when the same disease is in both lines (the dam's
// and the sire's side, counting the dogs, their full siblings and, when shown,
// half siblings), soft orange when it is in one line only. Purple: a full or
// half sibling has one, strong purple when that disease is in both lines.
// Half siblings are left out unless switched on.
// Blue: a carrier (DNA test «Bærer», the dog's own or a sibling's). A
// carrier is not a problem, so it never counts as a disease in both lines and
// any other finding on the card comes first.
// Clicking a dog opens the details.

const GENERATION_OPTIONS = [2, 3, 4];

// From this many generations the pill on a card shrinks to an icon
const COMPACT_FROM = 3;
const GENE_TESTS = ["GLAUKOM", "ATAKSI", "DWARF"];
// Weakest first. baerer only comes from the DNA tests (source "gene").
const STATUSES = ["baerer", "mistenkt", "diagnostisert"];
// The line states, the one that wins first
const LINE_STATES = ["both", "one", "carrier"] as const;
type LineState = typeof LINE_STATES[number];

class Diagnosis {
    key!: string;
    status!: string;
    source?: string;
    value?: string;
}

class Finding {
    kind!: "own" | "full" | "half";
    key!: string;
    status!: string;
}

function strongerFirst(a: { status: string, key: string }, b: { status: string, key: string }) {
    return STATUSES.indexOf(b.status) - STATUSES.indexOf(a.status) || a.key.localeCompare(b.key);
}

// One finding per disease among the given siblings, at its strongest status
function siblingFindings(kind: "full" | "half", siblings: any[]): Finding[] {
    const byKey: { [key: string]: Finding } = {};
    for (const sibling of siblings ?? []) {
        for (const diagnosis of (sibling.health ?? []) as Diagnosis[]) {
            const current = byKey[diagnosis.key];
            if (current == undefined || STATUSES.indexOf(diagnosis.status) > STATUSES.indexOf(current.status)) {
                byKey[diagnosis.key] = { kind: kind, key: diagnosis.key, status: diagnosis.status };
            }
        }
    }
    return Object.values(byKey).sort(strongerFirst);
}

// Own diagnoses first, then full siblings', then half siblings'
function findingsFor(dog: any): Finding[] {
    const own: Finding[] = ((dog.health ?? []) as Diagnosis[])
        .map(d => ({ kind: "own" as const, key: d.key, status: d.status }))
        .sort(strongerFirst);
    return [
        ...own,
        ...siblingFindings("full", dog.relatives?.full?.affected),
        ...siblingFindings("half", dog.relatives?.half?.affected),
    ];
}

// The diseases in a line: the dog and its ancestors shown, each with its own
// diagnoses and its affected full and half siblings (half siblings are already
// gone from the tree when they are switched off). Carriers do not count.
function lineDiseases(dog: any, keys: Set<string> = new Set()): Set<string> {
    if (!dog || dog._skeleton) return keys;
    const add = (d: Diagnosis) => { if (d.status != "baerer") keys.add(d.key); };
    for (const d of dog.health ?? []) add(d);
    for (const kind of ["full", "half"]) {
        for (const sibling of dog.relatives?.[kind]?.affected ?? []) {
            for (const d of sibling.health ?? []) add(d);
        }
    }
    lineDiseases(dog.dam, keys);
    lineDiseases(dog.sire, keys);
    return keys;
}

// A carrier on a dog shown or one of its siblings
function hasCarrier(dog: any): boolean {
    if (!dog || dog._skeleton) return false;
    const healthLists = [
        dog.health,
        ...(dog.relatives?.full?.affected ?? []).map((sibling: any) => sibling.health),
        ...(dog.relatives?.half?.affected ?? []).map((sibling: any) => sibling.health),
    ];
    return healthLists.some(health => ((health ?? []) as Diagnosis[]).some(d => d.status == "baerer"))
        || hasCarrier(dog.dam) || hasCarrier(dog.sire);
}

// The dog and its ancestors shown
function subtreeIds(dog: any, ids: Set<number> = new Set()): Set<number> {
    if (dog && !dog._skeleton) {
        ids.add(Number(dog.ANIMAL_ID));
        subtreeIds(dog.dam, ids);
        subtreeIds(dog.sire, ids);
    }
    return ids;
}

function hdColor(hd: string | null | undefined) {
    switch ((hd ?? "").toUpperCase()) {
        case "A":
        case "B": return "var(--bs-success)";
        case "C": return "var(--bs-warning)";
        case "D":
        case "E": return "var(--bs-danger)";
        default: return "#c4c4c4";
    }
}

// Worst result of a full sibling, gene tests and health values together
// 0 nothing registered, 1 clear, 2 carrier or suspected, 3 affected or diagnosed
const SEVERITY_COLORS = ["#d0d0d0", "var(--bs-success)", "var(--bs-warning)", "var(--bs-danger)"];

function geneSeverity(value: any) {
    const text = String(value ?? "").toLowerCase();
    if (text.includes("affisert")) return 3;
    if (text.includes("bærer")) return 2;
    return text != "" ? 1 : 0;
}

function healthSeverity(diagnosis: Diagnosis | undefined) {
    if (diagnosis == undefined) return 0;
    return diagnosis.status == "diagnostisert" ? 3 : 2;
}

// Worst result of a full sibling, gene tests and health values together
function siblingColor(sibling: any) {
    let severity = 0;
    for (const key of GENE_TESTS) severity = Math.max(severity, geneSeverity(sibling[key]));
    for (const diagnosis of (sibling.health ?? []) as Diagnosis[]) severity = Math.max(severity, healthSeverity(diagnosis));
    return SEVERITY_COLORS[severity];
}

function skeletonTree(depth: number): any {
    const dog: any = { _skeleton: true, id: Math.random().toString() };
    if (depth > 0) {
        dog.dam = skeletonTree(depth - 1);
        dog.sire = skeletonTree(depth - 1);
    }
    return dog;
}

// Every disease registered anywhere in the tree: on the dogs, their affected
// siblings or the full siblings behind the sibling bars
function diseaseKeys(dog: any, keys: Set<string> = new Set()): Set<string> {
    if (!dog || dog._skeleton) return keys;
    const healthLists = [
        dog.health,
        ...(dog.relatives?.full?.affected ?? []).map((sibling: any) => sibling.health),
        ...(dog.relatives?.half?.affected ?? []).map((sibling: any) => sibling.health),
        ...(dog.siblings ?? []).map((sibling: any) => sibling.health),
    ];
    for (const health of healthLists) {
        for (const diagnosis of (health ?? []) as Diagnosis[]) keys.add(diagnosis.key);
    }
    diseaseKeys(dog.dam, keys);
    diseaseKeys(dog.sire, keys);
    return keys;
}

// A copy of the tree without the hidden diseases, as if they were never
// registered: no pill, no colour, no orange line, not in the details. With
// hideHalf the half siblings go the same way.
function withoutDiseases(dog: any, hidden: Set<string>, hideHalf: boolean): any {
    if (!dog || dog._skeleton || (hidden.size == 0 && !hideHalf)) return dog;
    const keep = (health: Diagnosis[] | undefined) => (health ?? []).filter(d => !hidden.has(d.key));
    const affected = (siblings: any[] | undefined) => (siblings ?? [])
        .map(sibling => ({ ...sibling, health: keep(sibling.health) }))
        .filter(sibling => sibling.health.length > 0);
    return {
        ...dog,
        health: keep(dog.health),
        relatives: dog.relatives && {
            full: { ...dog.relatives.full, affected: affected(dog.relatives.full?.affected) },
            half: { ...dog.relatives.half, affected: hideHalf ? [] : affected(dog.relatives.half?.affected) },
        },
        siblings: dog.siblings?.map((sibling: any) => ({ ...sibling, health: keep(sibling.health) })),
        // null (unknown parent) and undefined (not fetched) are kept as they are
        dam: dog.dam && withoutDiseases(dog.dam, hidden, hideHalf),
        sire: dog.sire && withoutDiseases(dog.sire, hidden, hideHalf),
    };
}

function cx(...classes: (string | false | undefined | null)[]) {
    return classes.filter(Boolean).join(" ");
}

function PedigreeCard(props: PedigreeCardProps) {
    const t = useTranslate();
    const [open, setOpen] = useState<{ dog: any, target: HTMLElement }>();
    // The matrix of all full siblings' results, shown while hovering the bars
    // Sibling lists opened in the details, as "<dog id>:<full|half>"
    const [expandedLists, setExpandedLists] = useState<string[]>([]);
    const [siblingTooltip, setSiblingTooltip] = useState<{ siblings: any[], x: number, y: number, openLeft: boolean }>();

    const compact = props.generations >= COMPACT_FROM;
    const generationOptions = props.generationOptions ?? GENERATION_OPTIONS;
    const single = props.dogId != undefined || props.dog != undefined;

    // Diseases switched off with the toggles. Remembered as hidden, so a
    // disease that turns up after changing generations starts switched on.
    // The page can own them (hiddenDiseases + onHiddenDiseasesChange), e.g. so
    // a disease warning elsewhere on the page follows the same switches.
    const [ownHiddenDiseases, setOwnHiddenDiseases] = useState<Set<string>>(new Set());
    const hiddenDiseases = props.hiddenDiseases ?? ownHiddenDiseases;
    const setHiddenDiseases = props.onHiddenDiseasesChange ?? setOwnHiddenDiseases;
    // Half siblings are off until switched on. The page can own this too.
    const [ownShowHalf, setOwnShowHalf] = useState<boolean>(false);
    const showHalf = props.showHalfSiblings ?? ownShowHalf;
    const setShowHalf = props.onShowHalfSiblingsChange ?? setOwnShowHalf;
    // The toggles stay put while a new number of generations loads
    const lastDiseases = useRef<string[]>([]);
    if (!props.loading) {
        const keys = new Set<string>();
        for (const tree of [props.dog, props.dam, props.sire]) diseaseKeys(withoutDiseases(tree, new Set(), !showHalf), keys);
        lastDiseases.current = Array.from(keys).sort((a, b) => diseaseName(a).localeCompare(diseaseName(b), "nb"));
    }
    const diseases = lastDiseases.current;

    const dog = props.loading && props.dogId != undefined ? skeletonTree(props.generations) : withoutDiseases(props.dog, hiddenDiseases, !showHalf);
    const dam = props.loading && props.damId != undefined ? skeletonTree(props.generations) : withoutDiseases(props.dam, hiddenDiseases, !showHalf);
    const sire = props.loading && props.sireId != undefined ? skeletonTree(props.generations) : withoutDiseases(props.sire, hiddenDiseases, !showHalf);

    function toggleDisease(key: string) {
        const hidden = new Set(hiddenDiseases);
        if (hidden.has(key)) hidden.delete(key);
        else hidden.add(key);
        setHiddenDiseases(hidden);
        // The open details show the tree as it was
        setOpen(undefined);
    }

    // Ancestors on both the left and the right side (inbreeding). T, H and the
    // dog in the middle are not ancestors.
    const leftIds = props.loading ? new Set<number>() : single ? subtreeIds(dog?.dam) : new Set([...Array.from(subtreeIds(dam?.dam)), ...Array.from(subtreeIds(dam?.sire))]);
    const rightIds = props.loading ? new Set<number>() : single ? subtreeIds(dog?.sire) : new Set([...Array.from(subtreeIds(sire?.dam)), ...Array.from(subtreeIds(sire?.sire))]);
    const duplicates = new Set(Array.from(leftIds).filter(id => rightIds.has(id)));

    // Diseases in both lines: the dam's and the sire's side (T and H with
    // their ancestors, or the dam's and sire's line of the dog in the middle)
    const leftDiseases = props.loading ? new Set<string>() : lineDiseases(single ? dog?.dam : dam);
    const rightDiseases = props.loading ? new Set<string>() : lineDiseases(single ? dog?.sire : sire);
    const bothLines = new Set(Array.from(leftDiseases).filter(key => rightDiseases.has(key)));
    // The carrier entries in the legend only when there is a carrier to explain
    const carriers = !props.loading && [dog, dam, sire].some(tree => hasCarrier(tree));

    // The line from a dog towards T/H: strong orange when a dog in it has a
    // disease that is in both lines, soft orange for other own diagnoses, blue
    // for carriers
    function lineState(d: any): LineState | undefined {
        if (!d || d._skeleton) return undefined;
        const states: (LineState | undefined)[] = ((d.health ?? []) as Diagnosis[]).map(x =>
            x.status == "baerer" ? "carrier" : bothLines.has(x.key) ? "both" : "one");
        states.push(lineState(d.dam), lineState(d.sire));
        return strongestLine(states);
    }

    function strongestLine(states: (LineState | undefined)[]): LineState | undefined {
        return LINE_STATES.find(state => states.includes(state));
    }

    function lineClass(state: LineState | undefined) {
        switch (state) {
            case "both": return styles.affectedboth;
            case "one": return styles.affected;
            case "carrier": return styles.carried;
            default: return undefined;
        }
    }

    function diseaseName(key: string) {
        return t("dog.details.health.status." + key);
    }

    // «Epilepsi» -> «epilepsi» inside a sentence, but «LTV» stays «LTV»
    function diseaseInSentence(key: string) {
        const name = diseaseName(key);
        return name.length > 1 && name[1] == name[1].toLowerCase() ? name[0].toLowerCase() + name.slice(1) : name;
    }

    function findingText(finding: Finding) {
        if (finding.kind == "own") {
            const pill = finding.status == "mistenkt" ? "ownsuspected" : finding.status == "baerer" ? "owncarrier" : "own";
            return t("pedigreecard.pill." + pill, { disease: diseaseName(finding.key) });
        }
        return t("pedigreecard.pill." + finding.kind + (finding.status == "baerer" ? "carrier" : ""), { disease: diseaseInSentence(finding.key) });
    }

    function openDog(animalId: any) {
        setOpen(undefined);
        if (props.onOpenDog) props.onOpenDog(String(animalId));
        else window.open("/hunder/" + animalId, "_blank");
    }

    // root: T or H on the breeding pages, "center" for the dog on the dog page
    function drawCard(dog: any, root?: "female" | "male" | "center") {
        const rootSex = root == "center" ? undefined : root;
        if (dog._skeleton) {
            return <div className={cx(styles.skeleton, root && styles.root)} />;
        }
        // A disease in both lines is the one to show on the card: both lines
        // before one line, a disease before a carrier, own before siblings'
        const isCarrier = (f: Finding) => f.status == "baerer";
        const rank = (f: Finding) => (bothLines.has(f.key) && !isCarrier(f) ? 0 : 4) + (isCarrier(f) ? 2 : 0) + (f.kind == "own" ? 0 : 1);
        const findings = findingsFor(dog).sort((a, b) => rank(a) - rank(b));
        const first = findings[0];
        const both = first != undefined && bothLines.has(first.key);
        const state = first == undefined ? undefined
            : isCarrier(first) ? "carrier"
            : first.kind == "own" ? (both ? "ownboth" : "own")
            : (both ? "relativeboth" : "relative");
        const isDuplicate = !root && duplicates.has(Number(dog.ANIMAL_ID));
        const label = first ? findingText(first) + (findings.length > 1 ? " +" + (findings.length - 1) : "") : undefined;

        return (
            <div
                className={cx(styles.card, root && styles.root, state == "own" && styles.own, state == "ownboth" && styles.ownboth, state == "relative" && styles.relative, state == "relativeboth" && styles.relativeboth, state == "carrier" && styles.carrier, isDuplicate && styles.inbred, open?.dog.id == dog.id && styles.active)}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                    const target = e.currentTarget;
                    setOpen(open?.dog.id == dog.id ? undefined : { dog: dog, target: target });
                }}
                onKeyDown={(e) => {
                    if (e.key == "Enter" || e.key == " ") {
                        e.preventDefault();
                        setOpen({ dog: dog, target: e.currentTarget });
                    }
                }}
            >
                {rootSex && (
                    <span className={cx(styles.sexbadge, rootSex == "female" ? styles.female : styles.male)}>
                        {t(rootSex == "female" ? "pedigreecard.female" : "pedigreecard.male")}
                    </span>
                )}
                <div className={styles.cardmain}>
                    <div className={styles.regnumber}>{dog.ID_VALUE}</div>
                    <div className={styles.name} title={dog.NAME}>{dog.NAME}</div>
                    {dog.DATE_BIRTH != undefined && (
                        <div className={styles.birth}>{general.dateToString(new Date(dog.DATE_BIRTH))}</div>
                    )}
                    <div className={styles.meta}>
                        <span className={styles.hddot} style={{ background: hdColor(dog.HD) }} />
                        <span>{t("pedigreecard.hd")}: {dog.HD || "–"}</span>
                        {isDuplicate && (
                            <span className={styles.duplicate} title={t("pedigreecard.duplicatewarning")}>&#9888;</span>
                        )}
                    </div>
                </div>
                {label && (compact ? (
                    <span className={styles.findingicon} title={label}>{findings.length > 1 ? findings.length : "!"}</span>
                ) : (
                    <span className={styles.pill}>{label}</span>
                ))}
                {dog.siblings?.length > 0 && (
                    <div
                        className={styles.siblings}
                        onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const openLeft = rect.right + 8 + 360 > window.innerWidth;
                            setSiblingTooltip({ siblings: dog.siblings, x: openLeft ? rect.left - 8 : rect.right + 8, y: rect.top, openLeft: openLeft });
                        }}
                        onMouseLeave={() => setSiblingTooltip(undefined)}
                    >
                        {dog.siblings.map((sibling: any) => (
                            <span key={sibling.ANIMAL_ID} style={{ background: siblingColor(sibling) }} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function drawSiblingTooltip() {
        if (siblingTooltip == undefined) return null;
        // Gene tests always, health values only those some sibling has (the
        // positive gene tests are in health too, but already have a column)
        const healthKeys = Array.from(new Set<string>(siblingTooltip.siblings.flatMap((sibling: any) =>
            ((sibling.health ?? []) as Diagnosis[]).filter(d => d.source != "gene").map(d => d.key)
        ))).sort();
        return ReactDOM.createPortal(
            <div
                className={styles.siblingtooltip}
                style={{ left: siblingTooltip.x, top: siblingTooltip.y, transform: siblingTooltip.openLeft ? "translateX(-100%)" : undefined }}
            >
                <div className={styles.siblingtooltiptitle}>{t("pedigreecard.siblingbars", { count: siblingTooltip.siblings.length })}</div>
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            {GENE_TESTS.map(key => <th key={key}>{t("pedigreecard.genetests." + key)}</th>)}
                            {healthKeys.map(key => <th key={key}>{diseaseName(key)}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {siblingTooltip.siblings.map((sibling: any) => (
                            <tr key={sibling.ANIMAL_ID}>
                                <td className={styles.siblingtooltipid}>{sibling.ID_VALUE ?? sibling.ANIMAL_ID}</td>
                                {GENE_TESTS.map(key => (
                                    <td key={key} className={styles.siblingtooltipcell} style={{ background: SEVERITY_COLORS[geneSeverity(sibling[key])] }}>
                                        {sibling[key] ?? "–"}
                                    </td>
                                ))}
                                {healthKeys.map(key => {
                                    const diagnosis = ((sibling.health ?? []) as Diagnosis[]).find(d => d.key == key);
                                    return (
                                        <td key={key} className={styles.siblingtooltipcell} style={{ background: SEVERITY_COLORS[healthSeverity(diagnosis)] }}>
                                            {diagnosis ? t("dog.details.health.status.options." + diagnosis.status) : "–"}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>,
            document.body
        );
    }

    function drawSubtree(dog: any, side: "left" | "right", rootSex?: "female" | "male") {
        const hasParents = dog.dam != undefined || dog.sire != undefined;
        const branches = [dog.dam, dog.sire];
        return (
            <div className={cx(styles.subtree, side == "left" && styles.left)}>
                <div className={styles.nodecell}>{drawCard(dog, rootSex)}</div>
                {hasParents && (
                    <div className={cx(styles.parents, lineClass(strongestLine(branches.map(lineState))))}>
                        {branches.map((parent, index) => (
                            <div key={parent?.id ?? index} className={cx(styles.branch, lineClass(lineState(parent)))}>
                                {parent ? drawSubtree(parent, side) : <div className={styles.unknown}>{t("pedigreecard.unknown")}</div>}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function diagnosisText(d: Diagnosis) {
        if (d.status == "mistenkt") return t("pedigreecard.pill.ownsuspected", { disease: diseaseName(d.key) });
        if (d.status == "baerer") return t("pedigreecard.pill.owncarrier", { disease: diseaseName(d.key) });
        return diseaseName(d.key);
    }

    function statusClass(d: Diagnosis) {
        return d.status == "diagnostisert" ? styles.diagnosed : d.status == "baerer" ? styles.carriertext : styles.suspected;
    }

    // One line per sibling: name, shared parent, diseases. Number and birth
    // year are in the tooltip.
    function drawSibling(sibling: any) {
        const health = [...((sibling.health ?? []) as Diagnosis[])].sort(strongerFirst);
        const year = sibling.DATE_BIRTH != undefined ? new Date(sibling.DATE_BIRTH).getFullYear() : undefined;
        return (
            <li key={sibling.ANIMAL_ID}>
                <button
                    type="button"
                    className={styles.sibling}
                    title={[sibling.NAME, sibling.ID_VALUE, year].filter(Boolean).join(" · ") + "\n" + health.map(diagnosisText).join(", ")}
                    onClick={() => openDog(sibling.ANIMAL_ID)}
                >
                    <span className={styles.siblingname}>{sibling.NAME ?? sibling.ID_VALUE}</span>
                    {sibling.sharedParent == "dam" && <span className={styles.siblingvia}>{t("pedigreecard.details.viadam")}</span>}
                    {sibling.sharedParent == "sire" && <span className={styles.siblingvia}>{t("pedigreecard.details.viasire")}</span>}
                    <span className={styles.siblingdiseases}>
                        {health.map((d, index) => (
                            <React.Fragment key={d.key}>
                                {index > 0 && ", "}
                                <span className={statusClass(d)}>{diagnosisText(d)}</span>
                            </React.Fragment>
                        ))}
                    </span>
                </button>
            </li>
        );
    }

    // «Helsøsken med sykdom: (2 av 8 ▾)», the count opening the list below
    function drawRelatives(dog: any, kind: "full" | "half") {
        const relatives = dog.relatives?.[kind];
        const total = relatives?.total ?? 0;
        // Diagnosed before suspected, then by name
        const worst = (sibling: any) => Math.max(...sibling.health.map((d: Diagnosis) => STATUSES.indexOf(d.status)));
        const affected = [...(relatives?.affected ?? [])].sort((a: any, b: any) =>
            worst(b) - worst(a) || String(a.NAME ?? "").localeCompare(String(b.NAME ?? ""), "nb")
        );
        const listKey = dog.id + ":" + kind;
        const expanded = expandedLists.includes(listKey);
        const summary = t("pedigreecard.details.affectedof", { affected: affected.length, total: total });
        return (
            <>
                <div className={styles.detailsline}>
                    <strong>{t("pedigreecard.details." + kind)}:</strong>
                    {affected.length > 0 ? (
                        // The count opens the list
                        <button
                            type="button"
                            className={styles.pillbutton}
                            aria-expanded={expanded}
                            onClick={() => setExpandedLists(expanded ? expandedLists.filter(k => k != listKey) : [...expandedLists, listKey])}
                        >
                            {summary} {expanded ? "\u25B4" : "\u25BE"}
                        </button>
                    ) : (
                        <span className={styles.value}>{summary}</span>
                    )}
                </div>
                {expanded && affected.length > 0 && (
                    <ul className={styles.siblinglist}>{affected.map(drawSibling)}</ul>
                )}
            </>
        );
    }

    // Laid out like the cards on the dog page: title, label/value tables
    // between dividers, and a link at the bottom
    function drawDetails() {
        const dog = open?.dog;
        if (dog == undefined) return <></>;
        // The positive gene tests are in health too, but have their own rows
        const health = [...((dog.health ?? []) as Diagnosis[])].filter(d => d.source != "gene").sort(strongerFirst);
        const geneTests = GENE_TESTS.filter(key => dog[key] != undefined);
        return (
            <Popover.Body className={styles.details}>
                <div className={styles.detailsname} title={dog.NAME}>{dog.NAME}</div>
                <div className={styles.detailssub}>
                    {dog.ID_VALUE}
                    {dog.DATE_BIRTH != undefined && " · " + general.dateToString(new Date(dog.DATE_BIRTH))}
                </div>
                <hr />
                <table className={styles.detailstable}>
                    <tbody>
                        <tr>
                            <td><strong>{t("pedigreecard.hd")}:</strong></td>
                            <td>
                                <span className={styles.value}>{dog.HD || "–"}</span>
                                <span className={styles.hddot} style={{ background: hdColor(dog.HD) }} />
                            </td>
                        </tr>
                        {geneTests.map(key => (
                            <tr key={key}>
                                <td><strong>{t("pedigreecard.genetests." + key)}:</strong></td>
                                <td><span className={styles.value}>{dog[key]}</span></td>
                            </tr>
                        ))}
                        {health.map(d => (
                            <tr key={d.key}>
                                <td><strong>{diseaseName(d.key)}:</strong></td>
                                <td>
                                    <span className={cx(styles.value, statusClass(d))}>
                                        {t("dog.details.health.status.options." + d.status)}
                                        {d.source == "dunker" && d.value != "Epilepsi" && " (" + d.value + ")"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <hr />
                {drawRelatives(dog, "full")}
                {showHalf && drawRelatives(dog, "half")}
                <hr />
                <button type="button" className={styles.textlink} onClick={() => openDog(dog.ANIMAL_ID)}>
                    {t("pedigreecard.details.open")} &#8599;
                </button>
            </Popover.Body>
        );
    }

    return (
        <div className={styles.pedigree}>
            <div className={styles.header}>
                <div className={styles.controls}>
                    <div className={styles.hint}>
                        <span className={styles.infoicon}>i</span>
                        {t("pedigreecard.hint")}
                    </div>
                    <label className={styles.generations}>
                        {t("pedigreecard.generations")}
                        <select
                            value={props.generations}
                            onChange={(e) => props.onGenerationsChange(parseInt(e.target.value))}
                        >
                            {generationOptions.map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </label>
                    <div className={styles.diseasetoggles}>
                        <span>{t("pedigreecard.relatives")}</span>
                        <button
                            type="button"
                            className={cx(styles.diseasetoggle, showHalf && styles.shown)}
                            aria-pressed={showHalf}
                            onClick={() => { setShowHalf(!showHalf); setOpen(undefined); }}
                        >
                            {showHalf && <span className={styles.diseasetogglemark}>&#10003;</span>}
                            {t("pedigreecard.halfsiblings")}
                        </button>
                    </div>
                    {diseases.length > 0 && (
                        <div className={styles.diseasetoggles}>
                            <span>{t("pedigreecard.diseases")}</span>
                            {diseases.map(key => {
                                const shown = !hiddenDiseases.has(key);
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={cx(styles.diseasetoggle, shown && styles.shown)}
                                        aria-pressed={shown}
                                        onClick={() => toggleDisease(key)}
                                    >
                                        {shown && <span className={styles.diseasetogglemark}>&#10003;</span>}
                                        {diseaseName(key)}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className={styles.legend}>
                    <span><span className={cx(styles.legenddot, styles.ownboth)} />{t("pedigreecard.legend.ownboth")}</span>
                    <span><span className={cx(styles.legenddot, styles.own)} />{t("pedigreecard.legend.own")}</span>
                    <span><span className={cx(styles.legenddot, styles.relativeboth)} />{t("pedigreecard.legend.relativeboth")}</span>
                    <span><span className={cx(styles.legenddot, styles.relative)} />{t("pedigreecard.legend.relative")}</span>
                    {carriers && (
                        <span><span className={cx(styles.legenddot, styles.carrier)} />{t("pedigreecard.legend.carrier")}</span>
                    )}
                    {duplicates.size > 0 && (
                        <span><span className={styles.legendinbred}>&#9888;</span>{t("pedigreecard.legend.duplicate")}</span>
                    )}
                </div>
            </div>
            <div className={styles.scroller}>
                {single ? (
                    dog && (
                        <div className={cx(styles.bowtie, styles.single)}>
                            {dog.dam !== undefined && (
                                <>
                                    {dog.dam ? drawSubtree(dog.dam, "left") : <div className={styles.unknown}>{t("pedigreecard.unknown")}</div>}
                                    <span className={cx(styles.link, lineClass(lineState(dog.dam)))} />
                                </>
                            )}
                            <div className={styles.nodecell}>{drawCard(dog, "center")}</div>
                            {dog.sire !== undefined && (
                                <>
                                    <span className={cx(styles.link, lineClass(lineState(dog.sire)))} />
                                    {dog.sire ? drawSubtree(dog.sire, "right") : <div className={styles.unknown}>{t("pedigreecard.unknown")}</div>}
                                </>
                            )}
                        </div>
                    )
                ) : (
                    <div className={styles.bowtie}>
                        {dam && drawSubtree(dam, "left", "female")}
                        {sire && drawSubtree(sire, "right", "male")}
                    </div>
                )}
            </div>
            {drawSiblingTooltip()}
            <Overlay
                target={open?.target ?? null}
                show={open != undefined}
                placement="auto"
                rootClose
                onHide={(e: Event) => {
                    // A click on the open card is handled by the card itself
                    if (!open?.target.contains(e?.target as Node)) setOpen(undefined);
                }}
            >
                <Popover className={styles.popover}>
                    {drawDetails()}
                </Popover>
            </Overlay>
        </div>
    );
}

class PedigreeCardProps {
    // Trees from /api/statistics/dog/:id/familytree. Either dam and/or sire
    // (T and H, breeding pages) or dog (one dog in the middle, dog page).
    // undefined: no dog there
    dam?: any;
    sire?: any;
    dog?: any;
    // The dogs being fetched, so the right places get a skeleton while loading
    damId?: number;
    sireId?: number;
    dogId?: number;
    loading: boolean = false;
    generations!: number;
    onGenerationsChange!: (generations: number) => void;
    generationOptions?: number[];
    // Where a dog's link goes. Default: its dog page in a new tab
    onOpenDog?: (animalId: string) => void;
    // Diseases switched off, when the page keeps track of them
    hiddenDiseases?: Set<string>;
    onHiddenDiseasesChange?: (hidden: Set<string>) => void;
    // Whether half siblings count, when the page keeps track of it
    showHalfSiblings?: boolean;
    onShowHalfSiblingsChange?: (show: boolean) => void;
}

export default PedigreeCard;
